import { AenProviderFeeRecord } from "models/view-models/aen-resources/aen-provider-fee-record";
import { AenProviderFeeService } from "utilities/services/aen-resources/aen-provider-fee-service";
import { RoleType } from "models/enumerations/users/role-type";
import { ToastManager } from "utilities/toast/toast-manager";
import { TranslatedCopy } from "utilities/interfaces/culture-resources";
import { t } from "utilities/localization/t";
import { useCallback, useEffect, useState } from "react";
import { useErrorMessageState } from "utilities/hooks/use-error-state";

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const ERROR_LOADING: TranslatedCopy = "thereWasAnIssueLoadingAenProviderFees";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface UseAenProviderFeeHook {
    error?: Error;
    isLoading: boolean;
    aenProviderFee: AenProviderFeeRecord[];
    rowCount: number;
    addAenProviderFee: (aenProviderFee: AenProviderFeeRecord) => Promise<boolean>;
    updateAenProviderFee: (aenProviderFee: AenProviderFeeRecord) => Promise<boolean>;
    deleteAenProviderFee: (aenProviderFee: AenProviderFeeRecord) => Promise<boolean>;
}

interface UseAenProviderFeeHookOptions {
    onError?: (error: Error) => void;
    roleType?: RoleType;
    searchCriteriaReady?: boolean;
    searchText?: string;
    skip?: number;
    take?: number;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Hook
// -------------------------------------------------------------------------------------------------

export function useAenProviderFee({
    onError,
    roleType,
    searchText,
    skip,
    take,
    searchCriteriaReady = true,
}: UseAenProviderFeeHookOptions): UseAenProviderFeeHook {
    const { list } = AenProviderFeeService.useList();
    const { delete: deleteApi } = AenProviderFeeService.useDelete();
    const { update } = AenProviderFeeService.useUpdate();
    const { create } = AenProviderFeeService.useCreate();
    const [aenProviderFee, setAenProviderFee] = useState<AenProviderFeeRecord[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const [error, setError] = useErrorMessageState({ onError });
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const fetchData = useCallback(async () => {
        if (!searchCriteriaReady) {
            setAenProviderFee([]);
            setIsLoading(false);
            return;
        }

        try {
            (async function getAenProviderFee() {
                setIsLoading(true);
                const aenProviderFeeResponse = await list({
                });
                setIsLoading(false);

                if (
                    aenProviderFeeResponse?.resultObjects == null ||
                    aenProviderFeeResponse?.results == null ||
                    aenProviderFeeResponse.results.hasErrors()
                ) {
                    setError(t(ERROR_LOADING));
                    return;
                }

                setAenProviderFee(aenProviderFeeResponse.resultObjects);
                setRowCount(aenProviderFeeResponse.rowCount);
            })();
        } catch {
            setError(t(ERROR_LOADING));
        }
    }, [list, roleType, searchCriteriaReady, searchText, setError, skip, take]);

    const deleteAenProviderFee = async (aenProviderFee: AenProviderFeeRecord): Promise<boolean> => {
        try {
            if (aenProviderFee.id == null) {
                return false;
            }

            const deleteResponse = await deleteApi(aenProviderFee.id);

            if (!deleteResponse) {
                throw new Error("");
            }

            fetchData();

            ToastManager.success(t("contentDeletedSuccessfully"));
            return true;
        } catch {
            ToastManager.error(t("thereWasAnIssueDeletingTheContent"));
            return false;
        }
    };

    const addAenProviderFee = async (aenProviderFee: AenProviderFeeRecord): Promise<boolean> => {
        try {
            if (aenProviderFee?.id !== undefined) {
                throw new Error(t("thereWasAnErrorCreatingTheContent"));
            }
            const createAenProviderFeeResponse = await create(aenProviderFee);
            const createAenProviderFeeResult = createAenProviderFeeResponse?.result;

            if (
                createAenProviderFeeResult?.resultObject == null ||
                createAenProviderFeeResult.hasErrors()
            ) {
                throw new Error(t("thereWasAnErrorCreatingTheContent"));
            }

            fetchData();

            ToastManager.success(t("contentCreatedSuccessfully"));

            return true;
        } catch (error) {
            if (error === "") {
                ToastManager.error(t("thereWasAnErrorCreatingTheContent"));
            } else {
                ToastManager.error(`${error}`);
            }
            return false;
        }
    };

    const updateAenProviderFee = async (aenProviderFee: AenProviderFeeRecord): Promise<boolean> => {
        try {
            if (aenProviderFee?.id === 0 || aenProviderFee.id === undefined) {
                throw new Error(t("thereWasAnErrorSavingTheContent"));
            }
            const updateAenProviderFeeResponse = await update(aenProviderFee);
            const updateAenProviderFeeResult = updateAenProviderFeeResponse?.result;

            if (
                updateAenProviderFeeResult?.resultObject == null ||
                updateAenProviderFeeResult.hasErrors()
            ) {
                throw new Error(t("thereWasAnErrorSavingTheContent"));
            }

            fetchData();

            ToastManager.success(t("contentUpdatedSuccessfully"));

            return true;
        } catch (error) {
            if (error === "") {
                ToastManager.error(t("thereWasAnErrorSavingTheContent"));
            } else {
                ToastManager.error(`${error}`);
            }
            return false;
        }
    };

    useEffect(() => {
        fetchData();
    }, [searchText, skip, take, fetchData]);

    return {
        error,
        isLoading,
        aenProviderFee,
        rowCount,
        addAenProviderFee,
        updateAenProviderFee,
        deleteAenProviderFee,
    };
}

// #endregion Hook
